/**
 * chartA11y.ts — Pure helper functions for accessible recharts panel names and data tables.
 *
 * These functions are colocated with PanelCard.tsx and kept free of React / JSX so they
 * can be unit-tested without rendering the full component.
 *
 * Note: toLocaleString() is called with 'en-US' locale explicitly to avoid locale-dependent
 * test flakiness in CI environments. TCS is an English-language product.
 */

export interface ChartDataTable {
  /** "{panelTitle} \u2014 data table" (U+2014 em-dash) */
  caption: string
  /** "Category" for bar_chart | "Period" for line_chart */
  xHeader: string
  /** "Value" for all chart types */
  yHeader: string
  /** Pre-formatted { x: string; y: string } rows */
  rows: Array<{ x: string; y: string }>
  /** true when no data rows exist */
  empty: boolean
}

/**
 * buildChartAriaLabel — constructs the accessible name for a chart's ResponsiveContainer.
 *
 * @param chartType - 'bar_chart' | 'line_chart'
 * @param panelTitle - the panel's title string
 * @returns e.g. "Bar chart: Enrollment by session"
 */
export function buildChartAriaLabel(
  chartType: 'bar_chart' | 'line_chart',
  panelTitle: string
): string {
  const prefix = chartType === 'bar_chart' ? 'Bar chart' : 'Line chart'
  return `${prefix}: ${panelTitle}`
}

/**
 * buildChartDataTable — constructs the data structure for the sr-only data table.
 *
 * Column headers fall back to generic but meaningful defaults because ChartPanelData
 * does not carry xAxisLabel / yAxisLabel metadata today. See carry-forward item in TDD.
 *
 * Row count is driven by `labels.length` to match the visual chart — `PanelCard`
 * renders one chart point per label and coerces a missing value to 0. This helper
 * applies the same `?? 0` coercion so the sr-only table and the visual chart always
 * present the same number of data rows (important when the backend ever returns
 * `labels.length !== values.length`). Non-finite values (NaN, Infinity) are rendered
 * as "—".
 *
 * @param chartType - 'bar_chart' | 'line_chart'
 * @param panelTitle - the panel's title string
 * @param labels - x-axis label strings (already null-guarded by caller)
 * @param values - y-axis numeric values (already null-guarded by caller)
 */
export function buildChartDataTable(
  chartType: 'bar_chart' | 'line_chart',
  panelTitle: string,
  labels: string[],
  values: number[]
): ChartDataTable {
  const caption = `${panelTitle} \u2014 data table`
  const xHeader = chartType === 'bar_chart' ? 'Category' : 'Period'
  const yHeader = 'Value'

  const empty = labels.length === 0

  const rows: Array<{ x: string; y: string }> = []
  for (let i = 0; i < labels.length; i++) {
    // Mirror PanelCard's chart-point construction: missing values coerce to 0
    // so the table row count matches the bar/line point count exactly.
    const value = values[i] ?? 0
    const y = Number.isFinite(value) ? value.toLocaleString('en-US') : '\u2014'
    rows.push({ x: labels[i], y })
  }

  return { caption, xHeader, yHeader, rows, empty }
}
